import { Autocomplete, Box, Stack, Text } from '@mantine/core';
import { ParticipantAttendanceSelect } from '@/components/ParticipantAttendanceSelect';
import { normalizeCalendarClientAttendanceStatus } from '@/lib/calendarClientAttendanceStatus';
import type { ParticipantAttendanceFhirStatus } from '../utils/participantAttendance';
import {
  DEFAULT_PARTICIPANT_ATTENDANCE_STATUS,
  getParticipantAttendanceStatusLabel,
  locationRequiredForParticipantAttendance,
} from '../utils/participantAttendance';

export type ParticipantAttendanceRow = {
  patientId: string;
  displayName: string;
};

function locationRequiredForStatus(status: ParticipantAttendanceFhirStatus | string): boolean {
  const normalized =
    normalizeCalendarClientAttendanceStatus(status) ?? DEFAULT_PARTICIPANT_ATTENDANCE_STATUS;
  return locationRequiredForParticipantAttendance(normalized);
}

export function ParticipantAttendanceSection({
  rows,
  valueByPatientId,
  locationByPatientId = {},
  locationOptionsByPatientId = {},
  locationErrorsByPatientId = {},
  onChange,
  onLocationChange,
  readOnly = false,
  savedStatusByPatientId = {},
  requireResolvedAttendance = false,
}: {
  rows: ParticipantAttendanceRow[];
  valueByPatientId: Record<string, ParticipantAttendanceFhirStatus | undefined>;
  /** Baseline status when the note editor loaded — drives disabled transition options. */
  savedStatusByPatientId?: Record<string, ParticipantAttendanceFhirStatus | undefined>;
  locationByPatientId?: Record<string, string | undefined>;
  locationOptionsByPatientId?: Record<string, string[]>;
  locationErrorsByPatientId?: Record<string, string>;
  onChange: (patientId: string, status: ParticipantAttendanceFhirStatus) => void;
  onLocationChange?: (patientId: string, location: string | undefined) => void;
  readOnly?: boolean;
  /** When true (cohort shared notes), remind that unresolved attendance blocks sign/finalize. */
  requireResolvedAttendance?: boolean;
}): JSX.Element | null {
  if (rows.length === 0) {
    return null;
  }

  return (
    <Stack gap="sm">
      <Text size="sm" c="dimmed">
        Location is required for attended participants.
        {requireResolvedAttendance
          ? ' Shared group notes cannot be signed until every participant is Completed, No show, or Cancelled — Confirmed is not enough.'
          : null}
      </Text>

      <Stack gap="xs">
        {rows.map((row) => {
          const status = valueByPatientId[row.patientId] ?? DEFAULT_PARTICIPANT_ATTENDANCE_STATUS;
          const location = locationByPatientId[row.patientId];
          const locationError = locationErrorsByPatientId[row.patientId];
          const locationOptions = locationOptionsByPatientId[row.patientId] ?? [];
          const locationData = [
            ...new Set([...locationOptions, ...(location?.trim() ? [location.trim()] : [])]),
          ];
          const locationRequired = locationRequiredForStatus(status);
          const savedStatus =
            savedStatusByPatientId[row.patientId] ?? status ?? DEFAULT_PARTICIPANT_ATTENDANCE_STATUS;

          return (
            <Box
              key={row.patientId}
              p="md"
              style={{
                borderRadius: 'var(--mantine-radius-md)',
                border: '1px solid var(--mantine-color-gray-2)',
                backgroundColor: 'var(--mantine-color-gray-0)',
              }}
            >
              <Stack gap="sm">
                <Text size="sm" fw={600}>
                  {row.displayName}
                </Text>
                <Stack gap={6}>
                  <Text size="xs" c="dimmed" fw={600}>
                    Attendance
                  </Text>
                  {readOnly ? (
                    <Text size="sm" c="gray.7">
                      {getParticipantAttendanceStatusLabel(status)}
                    </Text>
                  ) : (
                    <Box data-note-field={`participant-attendance-${row.patientId}`}>
                      <ParticipantAttendanceSelect
                        aria-label={`Attendance for ${row.displayName}`}
                        value={status}
                        savedStatus={savedStatus}
                        onChange={(next) => onChange(row.patientId, next)}
                      />
                    </Box>
                  )}
                </Stack>
                <Stack gap={6}>
                  <Text size="xs" c="dimmed" fw={600}>
                    {locationRequired ? 'Location *' : 'Location'}
                  </Text>
                  {readOnly ? (
                    <Text size="sm" c="gray.7" truncate title={location?.trim() || undefined}>
                      {location?.trim() || '—'}
                    </Text>
                  ) : (
                    <Box data-note-field={`participant-location-${row.patientId}`}>
                      <Autocomplete
                        aria-label={`Location for ${row.displayName}`}
                        size="sm"
                        placeholder={locationRequired ? 'Required when attended' : 'Optional'}
                        data={locationData}
                        value={location ?? ''}
                        error={locationError}
                        required={locationRequired}
                        onChange={(v) =>
                          onLocationChange?.(row.patientId, v.trim() ? v.trim() : undefined)
                        }
                        clearable
                      />
                    </Box>
                  )}
                </Stack>
              </Stack>
            </Box>
          );
        })}
      </Stack>
    </Stack>
  );
}
